import { Autocomplete, Box, FormControl, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import { ButtonComponent, CommonModal, FunctionsEnum, TextFieldComponent } from '@watter/shared-components';
import { GetCountries, GetState } from 'react-country-state-city';
import { useDispatch } from 'react-redux';
import { userEnums } from '@watter/shared-types';
import { AntSwitch } from '../../components/common-styles';
import { NavigationConfig } from '../../utils/navigation-constants';
import { EditWrapper } from './manage-customer-styles';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { GET_CUSTOMER_DETAILS } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import { UPDATE_CUSTOMER } from '../../graphql/mutations';
import { openToast } from '../../store/reducers/toastSlice';
import { usePermissions } from '../../custom_hooks/permissions';
import { IFormObj } from './type';

const EditCustomer = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const customerData = location.state?.customerData;
    const dispatch = useDispatch();
    const [initialForm, setInitialForm] = useState<IFormObj>({
        firstName: '',
        lastName: '',
        email: '',
        mobile_number: '',
        street: '',
        city: '',
        zipCode: '',
        profileImageURI: null,
        state: '',
        phoneNumber: '',
        serialId: '',
        vastId: '',
    });
    const [countriesList, setCountriesList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [selectedCountryId, setCountryId] = useState(0);
    const [selectedStateId, setStateId] = useState(0);
    const [userCountryCode, setUserCountryCode] = useState('');
    const [userStateCode, setUserStateCode] = useState('');
    const [customerDetails, setCustomerDetails] = useState<any>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<any>(customerDetails?.status);
    const [errors, setErrors] = useState<any>({});
    const [selectedCountryPhoneCode, setSelectedCountryPhoneCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const { canEdit, canView } = usePermissions();

    React.useEffect(() => {
        GetCountries()?.then((result: any) => {
            const filteredCountries: any = [];
            result.forEach((country: any) => {
                if (country.name === 'India' || country.name === 'United States') {
                    filteredCountries.push({ ...country, phone_code: `+${country.phone_code}` });
                }
                setCountriesList(filteredCountries);
            });
        });
    }, []);
    React.useEffect(() => {
        const data: any = countriesList.filter((i: any) => i.iso2 === userCountryCode);
        setCountryId(data[0]?.id);
        setStateList([]);
        setStateId(0);
        setInitialForm({ ...initialForm, state: '' });
        GetState(data[0]?.id)?.then((result: any) => {
            setStateList(result);
        });
        // disabled eslint for this line as redundant re-rendering is happening
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCountryCode, countriesList]);
    React.useEffect(() => {
        const data: any = stateList.filter((i: any) => i.state_code === userStateCode);
        setStateId(data[0]?.id);
        setInitialForm({ ...initialForm, state: data[0]?.name });
        // disabled eslint for this line as it is resetting initial form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateList, userStateCode]);
    const getCustomerDetails = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_CUSTOMER_DETAILS,
                variables: { getCustomerDetailsId: customerData?.id },
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomerDetails) {
                const address = data?.getCustomerDetails.user_addresses?.[0] || {};
                setInitialForm({
                    ...initialForm,
                    firstName: data?.getCustomerDetails?.first_name,
                    lastName: data?.getCustomerDetails?.last_name,
                    email: data?.getCustomerDetails?.email,
                    mobile_number: data?.getCustomerDetails?.phone_number,
                    street: address.street,
                    city: address.city,
                    zipCode: address.zip_code,
                });
                setNewStatus(data?.getCustomerDetails?.status);
                setUserCountryCode(address.country);
                setUserStateCode(address.state);
                setCustomerDetails(data?.getCustomerDetails);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, customerData?.id, initialForm]);
    useEffect(() => {
        getCustomerDetails();
        // disabled eslint for this line as redundant API call is happening
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const formatPhoneNumber = (number: string, countryCode: string) => {
        const digits = number.replace(/\D/g, '');
        if (countryCode === '+1' || countryCode === '+91') {
            return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }

        return digits;
    };
    useEffect(() => {
        if (customerDetails?.phone_number) {
            const countryIso2 = customerDetails?.user_addresses?.[0]?.country || 'IN';
            const country: any = countriesList.find((c: any) => c.iso2 === countryIso2);

            if (country) {
                setSelectedCountryPhoneCode(country.phone_code);
                const rawNumber = customerDetails.phone_number.replace(country.phone_code, '').trim();
                setPhoneNumber(formatPhoneNumber(rawNumber, country.phone_code));
            } else {
                setSelectedCountryPhoneCode('');
                setPhoneNumber(customerDetails.phone_number);
            }
        }
    }, [customerDetails, countriesList]);

    const handleCountryChange = (e: any) => {
        setCountryId(e?.id);
        GetState(e?.id).then((result: any) => {
            setStateList(result);
        });
        initialForm.city = '';
        initialForm.street = '';
        initialForm.zipCode = '';
        setUserStateCode('');
    };
    const handleStateChange = (e: any) => {
        setStateId(e?.id);
        setInitialForm({ ...initialForm, state: e?.id });
        const data: any = stateList.filter((i: any) => i.id === e?.id);
        setUserStateCode(data[0]?.state_code);
    };

    const handleCancel = () => {
        navigate(NavigationConfig.ManageCustomer);
    };
    const getCountryCode = () => {
        const country: any = countriesList.filter((i: any) => i.id === selectedCountryId);
        return country[0]?.iso2;
    };
    const getStateCode = () => {
        const state: any = stateList.filter((i: any) => i.id === selectedStateId);
        return state[0]?.state_code;
    };
    const validateForm = () => {
        const newErrors: any = {};

        if (!initialForm.firstName?.trim()) {
            newErrors.firstName = `${t('first_name')} is required`;
        }

        if (!initialForm.lastName?.trim()) {
            newErrors.lastName = `${t('last_name')} is required`;
        }

        if (!initialForm.street?.trim()) {
            newErrors.street = `${t('address')} is required`;
        }

        if (!initialForm.city?.trim()) {
            newErrors.city = `${t('city')} is required`;
        }

        if (!initialForm.zipCode?.trim()) {
            newErrors.zipCode = `${t('zip_code')} is required`;
        } else if (!/^([1-9]\d{5}|\d{5}(-\d{4})?)$/.test(initialForm.zipCode.trim())) {
            newErrors.zipCode = 'Please enter a valid zip code';
        }

        if (!selectedCountryId) {
            newErrors.country = `${t('country')} is required`;
        }

        if (!selectedStateId) {
            newErrors.state = `${t('state')} is required`;
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const updateCustomer = async () => {
        try {
            dispatch(showLoader());

            const input: any = {
                user_addresses: {
                    zip_code: initialForm?.zipCode,
                    street: initialForm?.street,
                    state: getStateCode()?.toString(),
                    country: getCountryCode()?.toString(),
                    city: initialForm?.city,
                    address_type: customerDetails?.user_addresses?.[0]?.address_type,
                },
                // status: customerDetails?.status,
                last_name: initialForm?.lastName,
                id: customerData?.id,
                first_name: initialForm?.firstName,
            };

            const { data } = await client.mutate({
                mutation: UPDATE_CUSTOMER,
                variables: { input },
            });

            dispatch(
                openToast({
                    show: true,
                    message: data?.updateCustomer?.message,
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            handleCancel();
        } catch (err: any) {
            console.error('Update Installer failed', err);
            if (err.graphQLErrors && err.graphQLErrors[0]?.extensions?.code === '403') {
                // Handle forbidden: show message, redirect, etc.
                // console.log('Received Forbidden error:', err);
                if (canView(FunctionsEnum.MANAGE_CUSTOMERS)) {
                    navigate(NavigationConfig.ManageCustomer);
                } else {
                    navigate(NavigationConfig.Home);
                }
            } else {
                // Handle other errors
                console.error('Other error:', err);
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    const onSubmit = () => {
        if (validateForm()) {
            updateCustomer();
        }
    };

    const handleToggle = (checked: boolean) => {
        const status = checked ? userEnums.Status.active : userEnums.Status.inactive;
        setNewStatus(status);
        setModalOpen(true);
    };

    const handleStatusToggleApi = async (cognitoId: string, status: number, id: number) => {
        try {
            dispatch(showLoader());

            const input = {
                status,
                id,
            };

            try {
                const { data } = await client.mutate({
                    mutation: UPDATE_CUSTOMER,
                    variables: { input },
                });
                if (data?.updateCustomer) {
                    dispatch(
                        openToast({
                            show: true,
                            message: data?.updateCustomer?.message,
                            severity: 'success',
                            isAlertOpen: true,
                        }),
                    );
                    handleCancel();
                }
            } catch (err) {
                console.error('Error while changing the status:', err);
            }
        } catch (err) {
            console.error('Error while changing the status:', err);
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleConfirmStatusChange = async () => {
        if (newStatus === null) return;

        await handleStatusToggleApi(customerDetails.cognito_id.toString(), newStatus, customerDetails.id);

        setModalOpen(false);
        setNewStatus(null);
    };
    return (
        <EditWrapper>
            <Stack pb={2} direction="row" justifyContent="space-between" width="100%" alignItems="center">
                <Box display="flex" alignItems="center">
                    <Typography variant="h4">{t('edit_customer')}</Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography>{t('user_status')}</Typography>
                    <AntSwitch
                        disabled={!canEdit(FunctionsEnum.MANAGE_CUSTOMERS)}
                        checked={newStatus}
                        onChange={e => handleToggle(e.target.checked)}
                    />
                </Stack>
            </Stack>
            <Box className="listBox" mb={2}>
                <Box display="flex" alignItems="center" pb={2}>
                    <Typography variant="h5">{t('customer_details')}</Typography>
                </Box>
                <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('first_name')}{' '}
                            </Typography>
                            <TextFieldComponent
                                variant="outlined"
                                required
                                name="firstName"
                                type="text"
                                placeholder={`${t('enter_first_name')}`}
                                onChange={(e: any) => setInitialForm({ ...initialForm, firstName: e.target.value })}
                                value={initialForm.firstName}
                            />
                            {errors?.firstName && (
                                <Typography variant="caption" color="error">
                                    {errors?.firstName}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('last_name')}
                            </Typography>
                            <TextFieldComponent
                                variant="outlined"
                                required
                                id=""
                                error={false}
                                size="small"
                                name="lastName"
                                type="text"
                                placeholder={`${t('enter_last_name')}`}
                                onChange={(e: any) => setInitialForm({ ...initialForm, lastName: e.target.value })}
                                value={initialForm.lastName}
                            />
                            {errors?.lastName && (
                                <Typography variant="caption" color="error">
                                    {errors?.lastName}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack className="cstVerify cstMobileNumber cstNoBorder">
                            <Typography variant="subtitle1" color="info.100">
                                {t('mobile_no')}{' '}
                            </Typography>
                            <Box display="flex">
                                <FormControl className="cstSelectProfile" sx={{ width: '25%' }}>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        label="CountryCode"
                                        // placeholder={`${t('country_code')}`}
                                        value={selectedCountryPhoneCode}
                                        className="cstEditProfile"
                                        disabled
                                    >
                                        {countriesList.map((item: any) => (
                                            <MenuItem key={item.id} value={item.phone_code}>
                                                {item.phone_code}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextFieldComponent
                                        variant="outlined"
                                        id=""
                                        error={false}
                                        size="small"
                                        name="phoneNumber"
                                        type="string"
                                        disabled
                                        placeholder={`${t('enter_mobile_number')}`}
                                        onChange={(e: any) =>
                                            setInitialForm({ ...initialForm, mobile_number: e.target.value })
                                        }
                                        value={phoneNumber}
                                    />
                                </FormControl>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack className="cstEmail cstNoBorder">
                            <Typography variant="subtitle1" color="info.100">
                                {t('email_id')}
                            </Typography>
                            <TextFieldComponent
                                variant="outlined"
                                required
                                id=""
                                error={false}
                                size="small"
                                name="emailId"
                                type="text"
                                disabled
                                placeholder={`${t('enter_email')}`}
                                value={initialForm.email}
                                onChange={(e: any) => setInitialForm({ ...initialForm, email: e.target.value })}
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box className="listBox">
                <Box display="flex" alignItems="center" pb={2}>
                    <Typography variant="h5">{t('billing_address')}</Typography>
                </Box>
                <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }} display="flex">
                        <Stack width="100%">
                            <FormControl className="cstSelectProfile cstState">
                                <Typography variant="subtitle1" color="info.100" sx={{ height: 10 }}>
                                    {t('country')}
                                </Typography>
                                <Autocomplete
                                    options={countriesList}
                                    getOptionLabel={(option: any) => option.name || ''}
                                    value={
                                        selectedCountryId
                                            ? countriesList.find((country: any) => country.id === selectedCountryId)
                                            : ''
                                    }
                                    disableClearable
                                    onChange={(event, newValue) => handleCountryChange(newValue)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            placeholder={t('select_country')}
                                            InputLabelProps={{ sx: { display: 'none' } }}
                                        />
                                    )}
                                />
                                {errors?.country && (
                                    <Typography variant="caption" color="error">
                                        {errors?.country}
                                    </Typography>
                                )}
                            </FormControl>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('address')}{' '}
                            </Typography>
                            <TextFieldComponent
                                variant="outlined"
                                id=""
                                error={false}
                                size="small"
                                name="street"
                                type="text"
                                placeholder={`${t('enter_address')}`}
                                onChange={(e: any) => setInitialForm({ ...initialForm, street: e.target.value })}
                                value={initialForm.street}
                            />
                            {errors?.street && (
                                <Typography variant="caption" color="error">
                                    {errors?.street}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('city')}{' '}
                            </Typography>
                            <TextFieldComponent
                                variant="outlined"
                                id=""
                                error={false}
                                size="small"
                                name="city"
                                type="text"
                                placeholder={`${t('enter_city')}`}
                                onChange={(e: any) => setInitialForm({ ...initialForm, city: e.target.value })}
                                value={initialForm.city}
                            />
                            {errors?.city && (
                                <Typography variant="caption" color="error">
                                    {errors?.city}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} display="flex">
                        <Stack width="100%">
                            <FormControl className="cstSelectProfile cstState">
                                <Typography variant="subtitle1" color="info.100" sx={{ height: 10 }}>
                                    {t('state')}
                                </Typography>
                                <Autocomplete
                                    options={stateList}
                                    getOptionLabel={(option: any) => option.name || ''} // Specify the display label
                                    value={
                                        selectedStateId
                                            ? stateList.find((state: any) => state.id === selectedStateId)
                                            : ''
                                    }
                                    onChange={(event, newValue) => handleStateChange(newValue)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            placeholder={t('select_state')}
                                            InputLabelProps={{ sx: { display: 'none' } }}
                                        />
                                    )}
                                />
                                {errors?.state && (
                                    <Typography variant="caption" color="error">
                                        {errors?.state}
                                    </Typography>
                                )}
                            </FormControl>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('zip_code')}
                            </Typography>
                            <TextFieldComponent
                                variant="outlined"
                                id=""
                                error={false}
                                size="small"
                                name="zipCode"
                                type="text"
                                placeholder={`${t('enter_zip_code')}`}
                                onChange={(e: any) => setInitialForm({ ...initialForm, zipCode: e.target.value })}
                                value={initialForm.zipCode}
                            />
                            {errors?.zipCode && (
                                <Typography variant="caption" color="error">
                                    {errors?.zipCode}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                className="cstActionBtns"
                pt={2}
            >
                <Box>
                    <ButtonComponent
                        size="medium"
                        variant="contained"
                        fullWidth
                        buttonName={t('cancel')}
                        className="cstBtn"
                        onClick={handleCancel}
                    />
                </Box>
                <Box>
                    <ButtonComponent
                        size="medium"
                        variant="contained"
                        fullWidth
                        buttonName={t('save')}
                        className="cstBtn"
                        disabled={!canEdit(FunctionsEnum.MANAGE_CUSTOMERS)}
                        onClick={onSubmit}
                    />
                </Box>
            </Stack>
            <CommonModal
                modalOpen={modalOpen}
                header={`Do you want to ${newStatus === userEnums.Status.active ? 'activate' : 'inactivate'} this customer?`}
                cancelBtnName="Cancel"
                AcceptBtnName="Yes"
                closePopup={() => setModalOpen(false)}
                acceptBtn={handleConfirmStatusChange}
            />
        </EditWrapper>
    );
};
export default EditCustomer;
